/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef } from "react";
import { useFormState, Controller } from "react-hook-form";
import PropTypes from "prop-types";

import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";

import InputMask from "../../../InputMask";
import {
  generateFormFieldRules,
  getMaskProps,
  getParsedValue,
} from "../../services/form.service";

function FormTextField(props) {
  const {
    fieldProps,
    formProps,
    control,
    disabled,
    resetHandler,
    trigger,
    getValues,
  } = props;
  const { errors } = useFormState({
    control,
    name: fieldProps.key,
    exact: true,
  });

  const { key, label, validate, prefix, description } = fieldProps;

  const getAriaDescribedBy = () =>
    [
      description ? `${key}-description` : null,
      errors[key] ? `${key}-error` : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  const defaultValue = formProps[key] || fieldProps.defaultValue || "";
  const textFieldValueRef = useRef("");
  const inputMaskRef = useRef(null);

  if (!inputMaskRef.current) inputMaskRef.current = getMaskProps(fieldProps);

  const inputMaskProps = inputMaskRef.current;

  const condition = validate?.custom && getParsedValue(validate.custom, key);
  const disablePaste = condition?.disablePaste || false;

  const errorMap = {
    minLength: `${label} should contain minimum ${validate?.minLength} characters.`,
    maxLength: `Maximum ${validate?.maxLength} characters allowed.`,
    pattern: validate?.customMessage || "Invalid field input",
    required: "This is required",
    mask:
      validate?.customMessage ||
      inputMaskProps?.customMessage ||
      `${label} should contain minimum ${validate?.minLength} characters.`,
    confirmField: errors[key]?.message || validate?.customMessage,
  };

  const maskProps = {
    ...(inputMaskProps?.mask && {
      inputComponent: InputMask,
      inputProps: {
        mask: inputMaskProps?.mask,
        blocks: inputMaskProps?.blocks,
        showPlaceholder: inputMaskProps?.showPlaceholder || false,
        prefix,
      },
    }),
  };

  const removeExtraSpace = (s) => s.trim().split(/ +/).join(" ");
  const normalize = (value) => {
    if (value && fieldProps.properties?.normalize === "uppercase") {
      return value.toUpperCase();
    }
    return value;
  };

  const rules = generateFormFieldRules(fieldProps, getValues);

  return (
    <FormControl fullWidth margin="dense" sx={{ minHeight: "4.6875rem" }}>
      <Controller
        name={key}
        control={control}
        defaultValue={defaultValue}
        render={({ field: { onChange, value, ...field } }) => (
          <TextField
            {...field}
            id={fieldProps.key}
            name={fieldProps.key}
            label={fieldProps.label}
            aria-describedby={getAriaDescribedBy()}
            required={fieldProps.validate?.required}
            error={!!errors[key]}
            onFocus={(e) => {
              textFieldValueRef.current = e.target.value;
            }}
            onBlur={(e) => {
              if (e.target.value !== normalize(textFieldValueRef.current)) {
                onChange(removeExtraSpace(normalize(value)));
                resetHandler();
                textFieldValueRef.current = e.target.value;
              }
              trigger(key);
            }}
            onPaste={(e) => {
              if (disablePaste) e.preventDefault();
            }}
            onCopy={(e) => {
              if (disablePaste) e.preventDefault();
            }}
            value={inputMaskProps?.mask ? `${value}` : value}
            onChange={onChange}
            disabled={disabled}
            variant="standard"
            autoComplete={key}
            slotProps={{ input: inputMaskProps?.mask ? maskProps : undefined }}
          />
        )}
        rules={rules}
      />
      {description && (
        <FormHelperText
          id={`${fieldProps.key}-description`}
          sx={{ m: 0, fontSize: "0.875rem" }}
        >
          {description}
        </FormHelperText>
      )}
      {errors[key] && (
        <FormHelperText error id={`${fieldProps.key}-error`} sx={{ m: 0 }}>
          {(errors[key].type && errorMap[errors[key].type]) ||
            "This is required"}
        </FormHelperText>
      )}
    </FormControl>
  );
}

FormTextField.propTypes = {
  fieldProps: PropTypes.shape({
    conditional: PropTypes.shape({
      when: PropTypes.string.isRequired,
      eq: PropTypes.string.isRequired,
      show: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    }),

    type: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    defaultValue: PropTypes.string,
    description: PropTypes.string,
    prefix: PropTypes.string,
    inputMask: PropTypes.string,
    validate: PropTypes.shape({
      required: PropTypes.bool,
      minLength: PropTypes.number,
      maxLength: PropTypes.number,
      pattern: PropTypes.string,
      customMessage: PropTypes.string,
    }),
  }).isRequired,
  formProps: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  disabled: PropTypes.bool.isRequired,
  resetHandler: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
};
export default FormTextField;
